/**
 * section-handoff.tsx — Mục "Chuyển người" của màn nhân viên AI (Cần phát hành): công tắc, TỪ KHOÁ chuyển người
 * (`handoff.keywords` — tin khách chứa từ khoá → chuyển ngay, không gọi AI; "/mẫu/" = biểu thức chính quy), khung giờ được
 * chuyển, câu báo khách, điều kiện theo Ý định và theo Thuộc tính đã thu thập. Lưu vào nháp (settings.handoff).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Clock, Headset, KeyRound, Lightbulb, ListFilter, MessageSquareQuote, OctagonPause, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch, Textarea } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import { CollapsibleCard } from '../train/train-ui'
import { TimeWindowsEditor } from '../train/time-windows-editor'
import { ConditionBuilder } from '../train/condition-builder'
import { uniqueKey, type BotSettings, type HandoffIntent } from '../train/bot-settings'
import type { SectionProps } from '../train/draft-form'
import { StaffPanel } from './staff-ui'
import { HANDOFF_KEYWORD_MAX, addHandoffKeyword, isRegexKeyword } from './staff-lib'

type H = BotSettings['handoff']

// Tin báo mặc định lưu vào cấu hình bot và gửi khách (dữ liệu, không dịch).
const DEFAULT_NOTIFY_MESSAGE = 'Dạ em chuyển anh/chị sang nhân viên hỗ trợ ngay ạ, anh/chị đợi em một chút nhé.' // i18n-ignore

export function HandoffSection({ botId, form, setSettings, canEdit }: SectionProps) {
  const { t } = useTranslation('aiStaff')
  const h = form.settings.handoff
  const setH = (fn: (h: H) => H) => setSettings((x) => ({ ...x, handoff: fn(x.handoff) }))
  const disabled = !canEdit || !h.enabled
  const attrs = form.settings.collectAttributes

  return (
    <StaffPanel
      id="staff-chuyen-nguoi" title={t('handoff.title')} icon={<Headset className="h-4 w-4" />} scope="publish"
      description={t('handoff.description')}
      actions={
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          {h.enabled ? t('handoff.on') : t('handoff.off')}
          <Switch checked={h.enabled} onCheckedChange={(v) => setH((x) => ({ ...x, enabled: v }))} disabled={!canEdit} aria-label={t('handoff.enableAria')} />
        </label>
      }
    >
      <div className={cn('space-y-3', !h.enabled && 'pointer-events-none opacity-50')}>
        <CollapsibleCard title={t('handoff.keywords.title')} icon={<KeyRound className="h-4 w-4" />}
          description={t('handoff.keywords.description')}>
          <KeywordInput value={h.keywords ?? []} onChange={(k) => setH((x) => ({ ...x, keywords: k }))} disabled={disabled} />
        </CollapsibleCard>

        <CollapsibleCard title={t('handoff.window.title')} icon={<Clock className="h-4 w-4" />}
          description={t('handoff.window.description')}>
          <TimeWindowsEditor windows={h.timeWindows} onChange={(w) => setH((x) => ({ ...x, timeWindows: w }))} disabled={disabled} emptyText={t('handoff.window.empty')} />
        </CollapsibleCard>

        <CollapsibleCard title={t('handoff.notify.title')} icon={<MessageSquareQuote className="h-4 w-4" />}
          description={t('handoff.notify.description')}>
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" checked={h.notifyMessage === null} onChange={() => setH((x) => ({ ...x, notifyMessage: null }))} disabled={disabled} />
              {t('handoff.notify.silent')}
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={h.notifyMessage !== null} onChange={() => setH((x) => ({ ...x, notifyMessage: x.notifyMessage ?? DEFAULT_NOTIFY_MESSAGE }))} disabled={disabled} />
              {t('handoff.notify.send')}
            </label>
            {h.notifyMessage !== null && (
              <Textarea rows={2} maxLength={500} value={h.notifyMessage} onChange={(e) => setH((x) => ({ ...x, notifyMessage: e.target.value }))} disabled={disabled} />
            )}
          </div>
        </CollapsibleCard>

        <CollapsibleCard title={t('handoff.autoStop.title')} icon={<OctagonPause className="h-4 w-4" />}
          description={t('handoff.autoStop.description')}>
          <label className="flex items-start gap-3 text-sm">
            <Switch checked={h.onAutoStop} onCheckedChange={(v) => setH((x) => ({ ...x, onAutoStop: v }))} disabled={disabled} aria-label={t('handoff.autoStop.aria')} />
            <span className="min-w-0">
              <span className="font-medium">{t('handoff.autoStop.label')}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {t('handoff.autoStop.hint')}
              </span>
            </span>
          </label>
        </CollapsibleCard>

        <CollapsibleCard title={t('handoff.intents.title')} icon={<Lightbulb className="h-4 w-4" />}
          description={t('handoff.intents.description')}
          actions={canEdit ? <Button size="sm" variant="outline" disabled={disabled || h.intents.length >= 20}
            onClick={() => setH((x) => ({ ...x, intents: [...x.intents, { id: uniqueKey('y_dinh', x.intents.map((i) => i.id)), name: '', description: '', enabled: true }] }))}>
            <Plus className="h-3.5 w-3.5" /> {t('handoff.intents.add')}
          </Button> : undefined}>
          <IntentList intents={h.intents} onChange={(list) => setH((x) => ({ ...x, intents: list }))} disabled={disabled} />
        </CollapsibleCard>

        <CollapsibleCard title={t('handoff.attrs.title')} icon={<ListFilter className="h-4 w-4" />}
          description={t('handoff.attrs.description')}>
          <ConditionBuilder
            botId={botId}
            conditions={h.attributeRules}
            onChange={(r) => setH((x) => ({ ...x, attributeRules: r }))}
            attributes={attrs}
            disabled={disabled}
            newCondition={attrs[0] ? { variable: `agent.${attrs[0].key}`, op: '=', value: '' } : { variable: 'customer.tags', op: 'in', value: [] }}
          />
        </CollapsibleCard>
      </div>
    </StaffPanel>
  )
}

function KeywordInput({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled: boolean }) {
  const { t } = useTranslation('aiStaff')
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const add = () => {
    const r = addHandoffKeyword(value, draft)
    setError(r.error)
    if (!r.error) { onChange(r.list); setDraft('') }
  }
  return (
    <div className="space-y-1.5">
      <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border px-2 py-1.5">
        {value.map((k) => (
          <span key={k} className={cn('inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs', isRegexKeyword(k) ? 'bg-primary/10 font-mono text-primary' : 'bg-muted')}>
            {k}
            {!disabled && (
              <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => onChange(value.filter((x) => x !== k))} aria-label={t('handoff.keyword.remove', { keyword: k })}>×</button>
            )}
          </span>
        ))}
        <input
          className="min-w-[160px] flex-1 bg-transparent text-sm outline-none"
          value={draft}
          disabled={disabled || value.length >= HANDOFF_KEYWORD_MAX}
          placeholder={value.length >= HANDOFF_KEYWORD_MAX ? t('handoff.keyword.max', { max: HANDOFF_KEYWORD_MAX }) : t('handoff.keyword.placeholder')}
          onChange={(e) => { setDraft(e.target.value); setError(null) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add() }
            else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1))
          }}
          onBlur={() => { if (draft.trim()) add() }}
          aria-label={t('handoff.keyword.add')}
        />
      </div>
      <div className="flex justify-between text-[11px]">
        <span className={error ? 'text-destructive' : 'text-muted-foreground'}>{error ?? (value.length ? '' : t('handoff.keyword.none'))}</span>
        <span className="text-muted-foreground">{value.length}/{HANDOFF_KEYWORD_MAX}</span>
      </div>
    </div>
  )
}

function IntentList({ intents, onChange, disabled }: { intents: HandoffIntent[]; onChange: (l: HandoffIntent[]) => void; disabled: boolean }) {
  const { t } = useTranslation('aiStaff')
  const upd = (i: number, patch: Partial<HandoffIntent>) => onChange(intents.map((x, j) => (j === i ? { ...x, ...patch } : x)))
  if (!intents.length) return <p className="text-xs text-muted-foreground">{t('handoff.intents.none')}</p>
  return (
    <ul className="space-y-2">
      {intents.map((it, i) => (
        <li key={it.id} className="space-y-2 rounded-lg border p-3">
          <div className="flex items-center gap-2">
            <Switch checked={it.enabled} onCheckedChange={(v) => upd(i, { enabled: v })} disabled={disabled} />
            <Input className="h-8" maxLength={80} value={it.name}
              onChange={(e) => upd(i, { name: e.target.value })}
              placeholder={t('handoff.intents.namePlaceholder')} disabled={disabled} />
            <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => onChange(intents.filter((_, j) => j !== i))} disabled={disabled} aria-label={t('handoff.intents.remove')}>
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
          <Textarea rows={2} maxLength={500} value={it.description} onChange={(e) => upd(i, { description: e.target.value })}
            placeholder={t('handoff.intents.descPlaceholder')} disabled={disabled} />
        </li>
      ))}
    </ul>
  )
}
